<template>
  <section class="position-relative bg-white" id="crear_cuenta">
    <div class="container py-5 position-relative">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-5">
          <div v-if="aviso" class="alert small" :class="`alert-${aviso.tipo}`" role="alert">
            {{ aviso.texto }}
            <a v-if="aviso.login" :href="urlLogin" class="alert-link">Iniciar sesión</a>
          </div>

          <!-- Paso 1: formulario -->
          <template v-if="paso === 'formulario'">
            <h3 class="h3-custom">Crear cuenta</h3>
            <p class="text-muted small mb-0">
              Crea tu empresa demo. Los datos fiscales los completas después, al entrar.
            </p>
            <form class="row mt-3" novalidate @submit.prevent="enviarRegistro">
              <!-- Nombre -->
              <div class="col-md-6 form-group">
                <label for="nombre" class="form-label-custom">Nombre</label>
                <input
                  id="nombre"
                  v-model="form.nombre"
                  type="text"
                  maxlength="100"
                  autocomplete="given-name"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': errors.nombre }"
                  @input="errors.nombre = ''"
                />
                <div v-if="errors.nombre" class="invalid-feedback d-block">{{ errors.nombre }}</div>
              </div>

              <!-- Apellido -->
              <div class="col-md-6 form-group">
                <label for="apellido" class="form-label-custom">Apellido</label>
                <input
                  id="apellido"
                  v-model="form.apellido"
                  type="text"
                  maxlength="100"
                  autocomplete="family-name"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': errors.apellido }"
                  @input="errors.apellido = ''"
                />
                <div v-if="errors.apellido" class="invalid-feedback d-block">{{ errors.apellido }}</div>
              </div>

              <!-- Nombre de la empresa -->
              <div class="col-12 form-group">
                <label for="empresa" class="form-label-custom">Nombre de la empresa</label>
                <input
                  id="empresa"
                  v-model="form.empresa"
                  type="text"
                  maxlength="150"
                  autocomplete="organization"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': errors.empresa }"
                  @input="errors.empresa = ''"
                />
                <div v-if="errors.empresa" class="invalid-feedback d-block">{{ errors.empresa }}</div>
              </div>

              <!-- Alias (subdominio) -->
              <div class="col-12 form-group">
                <label for="alias" class="form-label-custom">Alias de la empresa</label>
                <input
                  id="alias"
                  type="text"
                  maxlength="30"
                  autocomplete="off"
                  autocapitalize="none"
                  spellcheck="false"
                  class="form-control form-control-sm"
                  :value="form.alias"
                  :class="{ 'is-invalid': errorAlias || estadoAlias === 'no_disponible' }"
                  @input="alEditarAlias"
                />
                <div class="d-flex justify-content-between align-items-start small mt-1">
                  <span class="text-muted text-break">{{ form.alias || "tu-empresa" }}.alliance-cloud.com</span>
                  <span v-if="estadoAlias === 'revisando'" class="text-muted text-nowrap ml-2">
                    <span class="spinner-border spinner-border-sm mr-1" aria-hidden="true"></span>Revisando…
                  </span>
                  <span v-else-if="estadoAlias === 'disponible'" class="text-success text-nowrap ml-2">
                    <i class="ri-checkbox-circle-line"></i> Disponible
                  </span>
                  <span v-else-if="estadoAlias === 'no_disponible'" class="text-danger text-nowrap ml-2">
                    <i class="ri-close-circle-line"></i> No disponible
                  </span>
                </div>
                <div v-if="errorAlias" class="invalid-feedback d-block">{{ errorAlias }}</div>
              </div>

              <!-- Correo electrónico -->
              <div class="col-md-6 form-group">
                <label for="email" class="form-label-custom">Correo electrónico</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  inputmode="email"
                  maxlength="150"
                  autocomplete="email"
                  autocapitalize="none"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': errors.email }"
                  @input="limpiarErrorEmail"
                />
                <div v-if="errors.email" class="invalid-feedback d-block">
                  {{ errors.email }}
                  <a v-if="correoRegistrado" :href="urlLogin">Iniciar sesión</a>
                </div>
              </div>

              <!-- Teléfono -->
              <div class="col-md-6 form-group">
                <label for="telefono" class="form-label-custom">Teléfono</label>
                <input
                  id="telefono"
                  v-model="form.telefono"
                  type="tel"
                  maxlength="30"
                  autocomplete="tel"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': errors.telefono }"
                  @input="errors.telefono = ''"
                />
                <div v-if="errors.telefono" class="invalid-feedback d-block">{{ errors.telefono }}</div>
              </div>

              <!-- Contraseña -->
              <div class="col-md-6 form-group">
                <label for="password" class="form-label-custom">Contraseña</label>
                <div class="input-group input-group-sm">
                  <input
                    id="password"
                    v-model="form.password"
                    :type="mostrarPassword ? 'text' : 'password'"
                    maxlength="100"
                    autocomplete="new-password"
                    class="form-control form-control-sm inputPassword"
                    :class="{ 'is-invalid': errors.password }"
                    @input="errors.password = ''"
                  />
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="input-group-text iconEye pointer"
                      :class="{ 'invalid-border': errors.password }"
                      :aria-label="mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                      @click="mostrarPassword = !mostrarPassword"
                    >
                      <i :class="mostrarPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                </div>
                <div v-if="errors.password" class="invalid-feedback d-block">{{ errors.password }}</div>
              </div>

              <!-- Repetir contraseña -->
              <div class="col-md-6 form-group">
                <label for="confirmPassword" class="form-label-custom">Repetir contraseña</label>
                <div class="input-group input-group-sm">
                  <input
                    id="confirmPassword"
                    v-model="form.confirmPassword"
                    :type="mostrarPasswordRepeat ? 'text' : 'password'"
                    maxlength="100"
                    autocomplete="new-password"
                    class="form-control form-control-sm inputPassword"
                    :class="{ 'is-invalid': errors.confirmPassword }"
                    @input="errors.confirmPassword = ''"
                  />
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="input-group-text iconEye pointer"
                      :class="{ 'invalid-border': errors.confirmPassword }"
                      :aria-label="mostrarPasswordRepeat ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                      @click="mostrarPasswordRepeat = !mostrarPasswordRepeat"
                    >
                      <i :class="mostrarPasswordRepeat ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                </div>
                <div v-if="errors.confirmPassword" class="invalid-feedback d-block">
                  {{ errors.confirmPassword }}
                </div>
              </div>

              <div class="col-12 text-center mt-3">
                <button type="submit" class="btn btn-primary px-5" :disabled="enviando">
                  <span v-if="enviando" class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></span>
                  {{ enviando ? "Enviando…" : "Crear cuenta" }}
                </button>
                <p class="small text-muted mt-3 mb-0">
                  ¿Ya tienes una cuenta? <a :href="urlLogin">Iniciar sesión</a>
                </p>
              </div>
            </form>
          </template>

          <!-- Paso 2: código del correo -->
          <template v-else-if="paso === 'codigo'">
            <h3 class="h3-custom">Revisa tu correo</h3>
            <p class="small mb-3">
              Te enviamos un código de 6 dígitos a <strong class="text-break">{{ pendiente.email }}</strong>.
              Escríbelo para crear tu empresa.
            </p>
            <form novalidate @submit.prevent="verificar">
              <div class="form-group text-center">
                <label for="codigo" class="form-label-custom d-block">Código</label>
                <input
                  id="codigo"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  autocomplete="one-time-code"
                  class="form-control codigo"
                  :value="codigo"
                  :class="{ 'is-invalid': errorCodigo }"
                  @input="alEscribirCodigo"
                />
                <div v-if="errorCodigo" class="invalid-feedback d-block">{{ errorCodigo }}</div>
              </div>
              <div class="text-center">
                <button type="submit" class="btn btn-primary px-5" :disabled="verificando">
                  <span v-if="verificando" class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></span>
                  {{ verificando ? "Verificando…" : "Verificar" }}
                </button>
              </div>
            </form>
            <div class="text-center small mt-4">
              <template v-if="pendiente.reenviosRestantes > 0">
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 align-baseline"
                  :disabled="segundosReenvio > 0 || reenviando || verificando"
                  @click="reenviar"
                >
                  {{ segundosReenvio > 0 ? `Reenviar código en ${segundosReenvio} s` : "Reenviar código" }}
                </button>
                <span class="text-muted">
                  ({{ pendiente.reenviosRestantes === 1 ? "te queda 1 reenvío" : `te quedan ${pendiente.reenviosRestantes} reenvíos` }})
                </span>
                <span class="text-muted mx-1">·</span>
              </template>
              <button
                type="button"
                class="btn btn-link btn-sm p-0 align-baseline"
                :disabled="verificando"
                @click="cambiarCorreo"
              >
                Cambiar correo
              </button>
            </div>
          </template>

          <!-- Paso 3: entrando a la empresa -->
          <div v-else class="text-center py-5">
            <div class="spinner-border text-primary mb-3" aria-hidden="true"></div>
            <h3 class="h4">Creando tu empresa…</h3>
            <p class="text-muted small mb-0">En un momento te llevamos a la configuración inicial.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  ALIAS_REGEX,
  ALIAS_RESERVADOS,
  URL_LOGIN,
  borrarPendiente,
  guardarPendiente,
  leerPendiente,
  reenviarCodigo,
  registrar,
  revisarAlias,
  sugerirAlias,
  urlCanje,
  verificarCodigo
} from "../../utils/registroDemo";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 8 a 100 caracteres con mayúscula, minúscula, número y símbolo (lo que no sea letra, número ni espacio)
const PASSWORD_REGEX = /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d)(?=.*[^\p{L}\p{N}\s]).{8,100}$/u;
const SEGUNDOS_REENVIO = 60;

// En el orden del formulario: el foco va al primero con error
const CAMPOS = ["nombre", "apellido", "empresa", "alias", "email", "telefono", "password", "confirmPassword"];

const MENSAJES = {
  aliasFormato: "Usa de 3 a 30 letras (sin acentos ni ñ), números o guiones, sin guion al inicio ni al final.",
  aliasReservado: "Ese alias está reservado. Elige otro.",
  aliasTomado: "Ese alias ya está en uso. Elige otro.",
  correoRegistrado: "Ya existe una cuenta con este correo.",
  password: "La contraseña debe tener de 8 a 100 caracteres, con al menos una mayúscula, una minúscula, un número y un símbolo."
};

// Errores 422 del registro: campo del API → campo del formulario y mensaje propio
const CAMPOS_DEL_API = {
  firstname: "nombre",
  lastname: "apellido",
  empresa_nombre: "empresa",
  alias: "alias",
  email: "email",
  telefono: "telefono",
  password: "password"
};
const MENSAJES_DEL_API = {
  nombre: "Revisa tu nombre: es obligatorio y admite hasta 100 caracteres.",
  apellido: "Revisa tu apellido: es obligatorio y admite hasta 100 caracteres.",
  empresa: "Revisa el nombre de la empresa: es obligatorio y admite hasta 150 caracteres.",
  alias: MENSAJES.aliasFormato,
  email: "Ingresa un correo electrónico válido, de hasta 150 caracteres.",
  telefono: "Revisa el teléfono: admite hasta 30 caracteres.",
  password: MENSAJES.password
};

export default {
  name: "Demo",
  data() {
    return {
      paso: "formulario",
      form: {
        nombre: "",
        apellido: "",
        empresa: "",
        alias: "",
        email: "",
        telefono: "",
        password: "",
        confirmPassword: ""
      },
      errors: {
        nombre: "",
        apellido: "",
        empresa: "",
        alias: "",
        email: "",
        telefono: "",
        password: "",
        confirmPassword: ""
      },
      correoRegistrado: false,
      // Mientras no lo edite, el alias sigue al nombre de la empresa
      aliasEditado: false,
      estadoAlias: "",
      turnoAlias: 0,
      temporizadorAlias: null,
      mostrarPassword: false,
      mostrarPasswordRepeat: false,
      enviando: false,
      aviso: null,
      // uuid del registro anterior al "Cambiar correo": el API libera su alias
      reemplaza: null,
      pendiente: null,
      codigo: "",
      errorCodigo: "",
      verificando: false,
      reenviando: false,
      ahora: Date.now(),
      reloj: null,
      urlLogin: URL_LOGIN
    };
  },
  computed: {
    pais() {
      return this.$route.meta.pais;
    },
    emailNormalizado() {
      return this.form.email.trim().toLowerCase();
    },
    // El correo cambia la respuesta del alias (su propia reserva sí está libre)
    emailParaAlias() {
      return EMAIL_REGEX.test(this.emailNormalizado) ? this.emailNormalizado : null;
    },
    errorAlias() {
      if (this.errors.alias) return this.errors.alias;
      const alias = this.form.alias;
      if (!alias) return "";
      if (ALIAS_RESERVADOS.includes(alias)) return MENSAJES.aliasReservado;
      // La sugerencia solo puede fallar por corta: se avisa al enviar
      if (this.aliasEditado && !ALIAS_REGEX.test(alias)) return MENSAJES.aliasFormato;
      return "";
    },
    segundosReenvio() {
      if (!this.pendiente || !this.pendiente.enviadoEn) return 0;
      const restante = this.pendiente.enviadoEn + SEGUNDOS_REENVIO * 1000 - this.ahora;
      return Math.max(0, Math.ceil(restante / 1000));
    }
  },
  watch: {
    "form.empresa"(nombre) {
      if (!this.aliasEditado) this.form.alias = sugerirAlias(nombre);
    },
    "form.alias"() {
      this.errors.alias = "";
      this.programarRevisionAlias();
    },
    emailParaAlias() {
      this.programarRevisionAlias();
    }
  },
  created() {
    // Recargar en el paso 2 no pierde el registro de esta misma sección
    const pendiente = leerPendiente();
    if (pendiente && pendiente.uuid && pendiente.pais === this.pais) {
      this.pendiente = pendiente;
      this.paso = "codigo";
      this.iniciarReloj();
    }
  },
  beforeUnmount() {
    clearTimeout(this.temporizadorAlias);
    this.detenerReloj();
  },
  methods: {
    alEditarAlias(evento) {
      const input = evento.target;
      const alias = input.value.toLowerCase();
      if (alias !== input.value) {
        const cursor = input.selectionStart;
        input.value = alias;
        input.setSelectionRange(cursor, cursor);
      }
      this.aliasEditado = true;
      this.form.alias = alias;
    },
    limpiarErrorEmail() {
      this.errors.email = "";
      this.correoRegistrado = false;
    },
    // Vale la última consulta: las respuestas de turnos anteriores se descartan.
    // Si falla, no bloquea el envío: el registro vuelve a revisar el alias
    programarRevisionAlias() {
      clearTimeout(this.temporizadorAlias);
      const turno = ++this.turnoAlias;
      const alias = this.form.alias;

      if (!ALIAS_REGEX.test(alias) || ALIAS_RESERVADOS.includes(alias)) {
        this.estadoAlias = "";
        return;
      }

      this.estadoAlias = "revisando";
      this.temporizadorAlias = setTimeout(async () => {
        try {
          const respuesta = await revisarAlias(alias, this.emailParaAlias, this.reemplaza);
          if (turno === this.turnoAlias) this.estadoAlias = respuesta.disponible ? "disponible" : "no_disponible";
        } catch (error) {
          if (turno === this.turnoAlias) this.estadoAlias = "";
        }
      }, 500);
    },
    validarFormulario() {
      const form = this.form;
      const errores = {};

      if (!form.nombre.trim()) errores.nombre = "Ingresa tu nombre.";
      if (!form.apellido.trim()) errores.apellido = "Ingresa tu apellido.";
      if (!form.empresa.trim()) errores.empresa = "Ingresa el nombre de la empresa.";

      if (!form.alias) errores.alias = "Ingresa el alias de la empresa.";
      else if (ALIAS_RESERVADOS.includes(form.alias)) errores.alias = MENSAJES.aliasReservado;
      else if (!ALIAS_REGEX.test(form.alias)) errores.alias = MENSAJES.aliasFormato;
      else if (this.estadoAlias === "no_disponible") errores.alias = MENSAJES.aliasTomado;

      if (!this.emailNormalizado) errores.email = "Ingresa tu correo electrónico.";
      else if (!EMAIL_REGEX.test(this.emailNormalizado)) errores.email = "Ingresa un correo electrónico válido.";

      if (!form.telefono.trim()) errores.telefono = "Ingresa tu número de teléfono.";

      if (!form.password) errores.password = "Ingresa una contraseña.";
      else if (!PASSWORD_REGEX.test(form.password)) errores.password = MENSAJES.password;

      if (!form.confirmPassword) errores.confirmPassword = "Repite la contraseña.";
      else if (form.password !== form.confirmPassword) errores.confirmPassword = "Las contraseñas no coinciden.";

      this.ponerErrores(errores);
      return Object.keys(errores).length === 0;
    },
    ponerErrores(errores) {
      CAMPOS.forEach(campo => {
        this.errors[campo] = errores[campo] || "";
      });
      this.correoRegistrado = false;

      const primero = CAMPOS.find(campo => errores[campo]);
      if (primero) this.enfocar(primero);
    },
    enfocar(id) {
      this.$nextTick(() => {
        const elemento = document.getElementById(id);
        if (elemento) elemento.focus();
      });
    },
    async enviarRegistro() {
      if (this.enviando) return;
      this.aviso = null;
      if (!this.validarFormulario()) return;

      this.enviando = true;
      try {
        const datos = {
          firstname: this.form.nombre.trim(),
          lastname: this.form.apellido.trim(),
          email: this.emailNormalizado,
          telefono: this.form.telefono.trim(),
          empresa_nombre: this.form.empresa.trim(),
          alias: this.form.alias,
          pais: this.pais,
          password: this.form.password
        };
        if (this.reemplaza) datos.reemplaza = this.reemplaza;

        const respuesta = await registrar(datos);

        this.reemplaza = null;
        this.irAlCodigo({
          uuid: respuesta.uuid,
          email: respuesta.email,
          pais: this.pais,
          reenviosRestantes: respuesta.reenviosRestantes,
          enviadoEn: Date.now()
        });
      } catch (error) {
        this.errorDelRegistro(error);
      } finally {
        this.enviando = false;
      }
    },
    errorDelRegistro(error) {
      if (error.code === "DEMOEMAILREGISTERED") {
        this.ponerErrores({ email: MENSAJES.correoRegistrado });
        this.correoRegistrado = true;
      } else if (error.code === "DEMOALIASTAKEN") {
        this.estadoAlias = "no_disponible";
        this.ponerErrores({ alias: MENSAJES.aliasTomado });
      } else if (error.status === 422 && error.errors) {
        const errores = {};
        let sinCampo = false;
        Object.keys(error.errors).forEach(campoApi => {
          const campo = CAMPOS_DEL_API[campoApi];
          if (campo) errores[campo] = MENSAJES_DEL_API[campo];
          else sinCampo = true;
        });
        this.ponerErrores(errores);
        if (sinCampo) {
          this.aviso = { tipo: "danger", texto: "No pudimos procesar el registro. Recarga la página e intenta de nuevo." };
        }
      } else {
        this.avisoGeneral(error);
      }
    },
    avisoGeneral(error) {
      let texto = "Algo salió mal. Intenta de nuevo en unos minutos.";
      if (error.status === 0) texto = "No pudimos conectar con Alliance. Revisa tu conexión e intenta de nuevo.";
      else if (error.status === 429) texto = "Demasiados intentos, espera un momento.";
      this.aviso = { tipo: "danger", texto };
    },
    irAlCodigo(pendiente) {
      this.pendiente = pendiente;
      guardarPendiente(pendiente);
      this.codigo = "";
      this.errorCodigo = "";
      this.paso = "codigo";
      this.iniciarReloj();
      window.scrollTo({ top: 0 });
      this.enfocar("codigo");
    },
    volverAlFormulario(aviso) {
      const email = this.pendiente ? this.pendiente.email : "";
      borrarPendiente();
      this.detenerReloj();
      this.pendiente = null;
      this.codigo = "";
      this.errorCodigo = "";
      this.paso = "formulario";
      this.aviso = aviso;
      // Tras recargar en el paso 2 solo se conserva el correo
      if (!this.form.email) this.form.email = email;
      window.scrollTo({ top: 0 });
    },
    cambiarCorreo() {
      this.reemplaza = this.pendiente.uuid;
      this.volverAlFormulario(null);
      this.enfocar("email");
      // Con el reemplazo, el alias de este registro vuelve a quedar libre
      this.programarRevisionAlias();
    },
    alEscribirCodigo(evento) {
      const input = evento.target;
      const codigo = input.value.replace(/\D/g, "").slice(0, 6);
      if (codigo !== input.value) input.value = codigo;
      this.codigo = codigo;
      this.errorCodigo = "";
    },
    async verificar() {
      if (this.verificando) return;
      this.aviso = null;

      if (!/^\d{6}$/.test(this.codigo)) {
        this.errorCodigo = "Escribe los 6 dígitos del código.";
        this.enfocar("codigo");
        return;
      }

      this.verificando = true;
      try {
        const respuesta = await verificarCodigo(this.pendiente.uuid, this.codigo);
        this.entrar(respuesta);
      } catch (error) {
        this.verificando = false;
        this.errorDeVerificacion(error);
      }
    },
    errorDeVerificacion(error) {
      const sinReenvios = this.pendiente.reenviosRestantes <= 0;
      const pedirOtro = sinReenvios ? "Usa «Cambiar correo» para empezar de nuevo." : "Pide uno nuevo.";

      if (error.code === "DEMOCODEINVALID") {
        this.errorCodigo = "El código no es correcto. Revísalo e intenta de nuevo.";
      } else if (error.code === "DEMOCODEEXPIRED") {
        this.errorCodigo = `El código venció. ${pedirOtro}`;
      } else if (error.code === "DEMOTOOMANYATTEMPTS") {
        this.errorCodigo = `Demasiados intentos con este código. ${pedirOtro}`;
      } else if (error.status === 422 && !error.code) {
        this.errorCodigo = "Escribe los 6 dígitos del código.";
      } else {
        if (!this.registroPerdido(error)) this.avisoGeneral(error);
        return;
      }
      this.enfocar("codigo");
    },
    // Errores en los que el registro ya no sirve: se borra lo guardado y se vuelve al formulario
    registroPerdido(error) {
      if (error.code === "DEMOSIGNUPNOTFOUND") {
        this.volverAlFormulario({ tipo: "warning", texto: "No encontramos tu registro. Llena el formulario de nuevo." });
      } else if (error.code === "DEMOALREADYVERIFIED") {
        this.volverAlFormulario({
          tipo: "warning",
          texto: "Este registro ya fue verificado. Entra con tu correo y contraseña.",
          login: true
        });
      } else if (error.code === "DEMOEMAILREGISTERED") {
        this.volverAlFormulario(null);
        this.ponerErrores({ email: MENSAJES.correoRegistrado });
        this.correoRegistrado = true;
      } else if (error.code === "DEMOALIASTAKEN") {
        this.reemplaza = this.pendiente.uuid;
        this.volverAlFormulario(null);
        this.estadoAlias = "no_disponible";
        this.ponerErrores({ alias: MENSAJES.aliasTomado });
      } else {
        return false;
      }
      return true;
    },
    async reenviar() {
      if (this.reenviando || this.segundosReenvio > 0) return;
      this.aviso = null;
      this.reenviando = true;
      try {
        const respuesta = await reenviarCodigo(this.pendiente.uuid);
        this.irAlCodigo({
          ...this.pendiente,
          reenviosRestantes: respuesta.reenviosRestantes,
          enviadoEn: Date.now()
        });
        this.aviso = { tipo: "success", texto: "Te enviamos un código nuevo." };
      } catch (error) {
        if (error.code === "DEMORESENDLIMIT") {
          this.pendiente = { ...this.pendiente, reenviosRestantes: 0 };
          guardarPendiente(this.pendiente);
          this.aviso = { tipo: "warning", texto: "Ya no se pueden enviar más códigos a este registro." };
        } else if (!this.registroPerdido(error)) {
          this.avisoGeneral(error);
        }
      } finally {
        this.reenviando = false;
      }
    },
    // Con la respuesta de verificar: la sesión la abre alliance.dev con el canje
    entrar(respuesta) {
      this.paso = "entrando";
      this.detenerReloj();
      borrarPendiente();
      window.location.replace(urlCanje(respuesta.alias, respuesta.canje));
    },
    iniciarReloj() {
      this.detenerReloj();
      this.ahora = Date.now();
      this.reloj = setInterval(() => {
        this.ahora = Date.now();
        if (this.segundosReenvio === 0) this.detenerReloj();
      }, 1000);
    },
    detenerReloj() {
      clearInterval(this.reloj);
      this.reloj = null;
    }
  }
};
</script>

<style scoped lang="scss">
.h3-custom {
  border-bottom: 1px solid #d4d4d4;
  padding-bottom: 10px;
}

.form-label-custom {
  margin-bottom: 1px;
  font-size: 13px;
}

.form-group {
  margin-bottom: 7px;
}

.iconEye {
  border-left: 0px;
  background-color: #fff;
}

.inputPassword {
  border-right: 0px;
}

.invalid-border {
  border-color: #dc3545 !important;
}

.pointer {
  cursor: pointer;
}

.codigo {
  max-width: 220px;
  margin: 0 auto;
  font-size: 1.5rem;
  letter-spacing: 0.4em;
  text-align: center;
}
</style>
